import React, { useRef } from "react";
import PageHeader from "../components/common/PageHeader";
import MetaData from "../helpers/MetaData";
import { useForm } from "react-hook-form";
import { useState, useCallback, useEffect, useContext } from "react";
import notification from "../helpers/notification";
import axios from "axios";
import ApiService from "../api/ApiService";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import AppContext from "../context/AppContext";
import GoToTop from "../helpers/GoToTop";

const BhadraPurnimaCheckout = () => {
    const { id } = useParams();
    const [top, setTop] = useState(0);
    const location = useLocation();
    const price = location.state?.price;
    const bookDetails = location.state?.bookDetails;
    const [searchParams, setSearchParams] = useSearchParams();
    const purchaseId = searchParams.get("shopId");
    const { user } = useContext(AppContext);
    const [selectedState, setSelectedState] = useState("");
    const [selectedValue, setSelectedStateValue] = useState(0);
    const [selectedStaticValue, setSelectedStaticValue] = useState(0);
    const [selectedLabelValue, setSelectedOptionValue] = useState("");
    const [deliveryOption, setDeliveryOption] = useState("delivery"); // "delivery" or "pickup"

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            amount: price || 0,
        },
    });

    const stateOptions = [
        { stateid: 1, label: "Andhra Pradesh", value: 360 },
        { stateid: 2, label: "Arunachal Pradesh", value: 450 },
        { stateid: 3, label: "Assam", value: 450 },
        { stateid: 4, label: "Bihar", value: 360 },
        { stateid: 5, label: "Chhattisgarh", value: 360 },
        { stateid: 6, label: "Goa", value: 360 },
        { stateid: 7, label: "Gujarat", value: 360 },
        { stateid: 8, label: "Haryana", value: 360 },
        { stateid: 9, label: "Himachal Pradesh", value: 360 },
        { stateid: 10, label: "Jammu and Kashmir", value: 450 },
        { stateid: 11, label: "Jharkhand", value: 360 },
        { stateid: 12, label: "Karnataka", value: 360 },
        { stateid: 13, label: "Kerala", value: 360 },
        { stateid: 14, label: "Madhya Pradesh", value: 360 },
        { stateid: 15, label: "Maharashtra", value: 275 },
        { stateid: 16, label: "Manipur", value: 450 },
        { stateid: 17, label: "Meghalaya", value: 450 },
        { stateid: 18, label: "Mizoram", value: 450 },
        { stateid: 19, label: "Nagaland", value: 450 },
        { stateid: 20, label: "Odisha", value: 360 },
        { stateid: 21, label: "Punjab", value: 360 },
        { stateid: 22, label: "Rajasthan", value: 360 },
        { stateid: 23, label: "Sikkim", value: 360 },
        { stateid: 24, label: "Tamil Nadu", value: 360 },
        { stateid: 25, label: "Telangana", value: 360 },
        { stateid: 26, label: "Tripura", value: 450 },
        { stateid: 27, label: "Uttar Pradesh", value: 360 },
        { stateid: 28, label: "Uttarakhand", value: 360 },
        { stateid: 29, label: "West Bengal", value: 360 },
    ];

    const reCaptChaRef = useRef();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [isRetryPayment, setIsRetryPayment] = useState(false);

    const fetchStateCity = async (pin) => {
        try {
            let url = `https://api.postalpincode.in/pincode/${pin}`;
            let config = {
                method: "GET",
                url,
            };
            const { data } = await axios(config);
            if (data[0].Status !== "Success") {
                notification("warning", "Enter valid PIN code!");
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleStateChange = (event) => {
        const selectedStateId = parseInt(event.target.value);
        const state = stateOptions.find((option) => option.stateid === selectedStateId);

        // Calculate total quantity from bookDetails
        const totalQuantity = bookDetails ? bookDetails.reduce((total, book) => total + book.quantity, 0) : 1;

        // Calculate delivery charge: base charge * total quantity
        const baseDeliveryCharge = state.value;
        const calculatedDeliveryCharge = baseDeliveryCharge * totalQuantity;

        setSelectedState(selectedStateId);
        setSelectedStateValue(calculatedDeliveryCharge); // This now includes quantity multiplier
        setSelectedStaticValue(baseDeliveryCharge); // Keep base charge for reference if needed
        setSelectedOptionValue(state.label);
    };

    const handleDeliveryOptionChange = (option) => {
        setDeliveryOption(option);
        if (option === "pickup") {
            // Reset delivery charges for pickup
            setSelectedStateValue(0);
            setSelectedStaticValue(0);
            setSelectedState("");
            setSelectedOptionValue("");
        }
    };

    const totalPrice = deliveryOption === "pickup"
        ? Math.round(price)
        : Math.round(price) + Math.round(selectedValue);

    // Generate product name from book details
    const getProductName = () => {
        if (!bookDetails || bookDetails.length === 0) return "Bhadra Purnima Purchase";

        return bookDetails.map(book => `${book.language} (Qty: ${book.quantity})`).join(", ");
    };

    const handlePurchase = async (values) => {
        try {
            setLoading(true);

            const token = await reCaptChaRef.current.executeAsync();
            values.token = token;
            values.amount = totalPrice;
            values.count = 1; // Fixed count for Bhadra Purnima
            values.userType = user ? "user" : "guest";
            values.productName = "Bhadra Purnima Purchase - " + getProductName();
            values.product_code = "BHADRA_PURNIMA";
            values.deliveryOption = deliveryOption;
            // Modify address based on delivery option
            if (deliveryOption === "delivery") {
                values.state = selectedLabelValue;
                // Concatenate streetName and areaName for address
                values.address = `${values.streetName || ''}, ${values.areaName || ''}`.trim().replace(/^,|,$/g, '');
            } else if (deliveryOption === "pickup") {
                // Set address as "Temple Pickup" for pickup option
                values.address = "Temple Pickup";
            }
            values.bookDetails = JSON.stringify(bookDetails); // Add book details to the request

            localStorage.setItem("order_details", "bhadra-purnima");

            const res = await ApiService.purchaseShopProduct(values);
            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY,
                currency: res.currency,
                amount: res.amount,
                name: process.env.REACT_APP_PROJECT_NAME,
                description: "Bhadra Purnima Purchase",
                image: `${process.env.REACT_APP_BACKEND_URL}/assets/img/logo.png`,
                order_id: res.id,
                handler: function (response) {
                    navigate(`/purchase-success/${res.shopId}`, { replace: true });
                },
                prefill: {
                    name: res.name ? res.name : "",
                    email: res.email ? res.email : "",
                    contact: res.phone ? res.phone : "",
                },
                notes: {
                    type: "shop",
                },
                theme: {
                    color: "#FFD370",
                },
                modal: {
                    ondismiss: async function () {
                        const cancelPurchase = await ApiService.cancelPurchase(res.id);
                    },
                },
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();

            paymentObject.on("payment.failed", function (response) {
                navigate(`/purchase-failed?id=${res.shopId}`, { replace: true });
            });
            setLoading(false);
            reCaptChaRef.current.reset();
        } catch (error) {
            setLoading(false);
            console.log(error);
            notification("error", error.response.data.message);
        }
    };

    const loadScript = (src) => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = src;
            script.onload = () => {
                resolve(true);
            };
            script.onerror = () => {
                resolve(false);
            };
            document.body.appendChild(script);
        });
    };

    useEffect(() => {
        loadScript("https://checkout.razorpay.com/v1/checkout.js");
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);


    useEffect(() => {
        if (user && !isRetryPayment) {
            reset({
                name: user.name ? user.name : "",
                phone: user.phone ? user.phone : "",
                email: user.email ? user.email : "",
                streetName: user.address ? user.address : "",
                areaName: user.area ? user.area : "",
                city: user.city ? user.city : "",
                pin: user.pin ? user.pin : "",
            });
        }
    }, [user]);

    const festRef = useCallback((node) => {
        if (node) {
            setTop(node.offsetTop + 600);
        }
    }, []);

    return (
        <>
            <MetaData title="Bhadra Purnima Purchase - ISKCON of Attapur" />
            {/* <PageHeader title="Bhadra Purnima Purchase" imgSrc="/images/page-header/24.jpg" /> */}
            <section className="donation-form-sec mobile-margin-top">
                <div className="container" ref={festRef}>
                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            <div className="heading">
                                <h2 className="head-1">Bhadra Purnima Purchase</h2>
                                <p className="b-line">
                                    Please complete your Bhādra Purnimā purchase of the Śrīmad-Bhāgavatam—your books will be delivered to your address, or as per your selected option.
                                </p>
                            </div>
                        </div>
                    </div>
                    <form className="shop-form mt-5" onSubmit={handleSubmit(handlePurchase)}>
                        <div className="row align-items-start">
                            <div className="col-lg-4">
                                <div className="donate-img sticky-top" style={{ top: "100px" }}>
                                    <figure className="mb-0">
                                        <img
                                            src="/images/campaign/bhadra-donate.jpeg"
                                            alt="Bhadra Purnima"
                                            className="img-fluid rounded shadow"
                                        />
                                    </figure>
                                </div>
                            </div>

                            <div className="col-lg-8 mt-lg-0 mt-4">
                                <div className="row">
                                    {/* Delivery Option Selection */}

                                    <div className="col-md-6 mb-3">
                                        <div className="type_checkbox mt-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="delivery"
                                                checked={deliveryOption.includes("delivery")}
                                                onChange={() => handleDeliveryOptionChange("delivery")}
                                            />
                                            <label className="form-check-label" htmlFor="delivery">
                                                Home Delivery
                                            </label>
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <div className="type_checkbox mt-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="pickup"
                                                checked={deliveryOption.includes("pickup")}
                                                onChange={() => handleDeliveryOptionChange("pickup")}
                                            />
                                            <label className="form-check-label" htmlFor="pickup">
                                                Temple Pickup
                                            </label>
                                        </div>
                                    </div>



                                    <div className="col-md-6">
                                        <div className="form-part">
                                            <label htmlFor="name">Name</label>
                                            <input
                                                type="text"
                                                placeholder="Enter Your Name"
                                                {...register("name", {
                                                    required: true,
                                                })}
                                            />
                                            {errors.name?.type === "required" && <span className="error text-danger">This is required</span>}
                                        </div>
                                    </div>



                                    <div className="col-md-6">
                                        <div className="form-part">
                                            <label htmlFor="phone">Phone</label>
                                            <input
                                                type="tel"
                                                placeholder="Enter Your Phone"
                                                maxLength={10}
                                                onKeyPress={(event) => {
                                                    if (!/[0-9]/.test(event.key)) {
                                                        event.preventDefault();
                                                    }
                                                }}
                                                {...register("phone", {
                                                    required: true,
                                                    pattern: {
                                                        value: /^[6-9]\d*$/,
                                                        message: "Please provide valid phone number",
                                                    },
                                                    minLength: {
                                                        value: 10,
                                                        message: "Phone number must be minimum 10 digit",
                                                    },
                                                })}
                                            />
                                            {errors.phone?.type === "required" && <span className="error text-danger">This is required</span>}
                                            {errors.phone && <span className="error text-danger">{errors.phone.message}</span>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-part">
                                            <label htmlFor="email">Email</label>
                                            <input
                                                type="email"
                                                placeholder="Enter Your Email"
                                                {...register("email", {
                                                    required: true,
                                                    pattern: {
                                                        value:
                                                            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                                        message: "Please enter a valid email",
                                                    },
                                                })}
                                            />
                                            {errors.email?.type === "required" && <span className="error text-danger">This is required</span>}
                                            {errors.email && <span className="error text-danger">{errors.email.message}</span>}
                                        </div>
                                    </div>

                                    {/* Address fields - only show for delivery option */}
                                    {deliveryOption === "delivery" && (
                                        <>
                                            <div className="col-md-6">
                                                <div className="form-part">
                                                    <label htmlFor="streetName">Street Name</label>
                                                    <textarea
                                                        {...register("streetName", {
                                                            required: deliveryOption === "delivery",
                                                        })}
                                                        placeholder="Enter Street Name"
                                                    ></textarea>
                                                    {errors.streetName?.type === "required" && <span className="error text-danger">This is required</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-part">
                                                    <label htmlFor="areaName">Area Name</label>
                                                    <textarea
                                                        {...register("areaName", {
                                                            required: deliveryOption === "delivery",
                                                        })}
                                                        placeholder="Enter Area"
                                                    ></textarea>
                                                    {errors.areaName?.type === "required" && <span className="error text-danger">This is required</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-part">
                                                    <label htmlFor="city">City</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Your City"
                                                        {...register("city", {
                                                            required: deliveryOption === "delivery",
                                                        })}
                                                    />
                                                    {errors.city?.type === "required" && <span className="error text-danger">This is required</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-part">
                                                    <label htmlFor="state">State</label>
                                                    <select
                                                        id="state"
                                                        name="state"
                                                        {...register("state", { required: deliveryOption === "delivery" })}
                                                        value={selectedState}
                                                        onChange={handleStateChange}
                                                    >
                                                        <option value="" disabled>
                                                            Select State
                                                        </option>
                                                        {stateOptions.map((option) => (
                                                            <option key={option.stateid} value={option.stateid}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.state?.type === "required" && <span className="error text-danger">This is required</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-part">
                                                    <label htmlFor="pin">PIN</label>
                                                    <input
                                                        type="text"
                                                        minLength={6}
                                                        maxLength={6}
                                                        onKeyPress={(event) => {
                                                            if (!/[0-9]/.test(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                        {...register("pin", {
                                                            required: deliveryOption === "delivery",
                                                            pattern: {
                                                                value: /^\d+$/,
                                                                message: "PIN code must be number.",
                                                            },
                                                            onChange: (e) => {
                                                                if (e.target.value.length === 6) {
                                                                    fetchStateCity(e.target.value);
                                                                }
                                                            },
                                                        })}
                                                        placeholder="PIN"
                                                    />
                                                    {errors.pin?.type === "required" && <span className="error text-danger">This is required</span>}
                                                    {errors.pin && <small className="text-danger error">{errors.pin.message}</small>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-part">
                                                    <label htmlFor="deliveryCharge">Delivery Charge</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Delivery Charge"
                                                        value={selectedValue > 0 ?
                                                            `₹${selectedValue} (₹${selectedStaticValue} × ${bookDetails ? bookDetails.reduce((total, book) => total + book.quantity, 0) : 1} qty)`
                                                            : '₹0'
                                                        }
                                                        readOnly
                                                        {...register("deliveryCharge")}
                                                    />
                                                    {selectedValue > 0 && (
                                                        <small className="text-muted">
                                                            Base charge: ₹{selectedStaticValue} per quantity
                                                        </small>
                                                    )}
                                                </div>
                                            </div>


                                            <div className="col-md-6">
                                                <div className="form-part">
                                                    <label htmlFor="deliveryCharge">Selected Books</label>
                                                    {bookDetails.map((book, index) => (
                                                        <div
                                                            key={index}
                                                            className="book-detail-item mb-2 p-2 border rounded"
                                                        >
                                                            <strong>{book.language}</strong> — Quantity: {book.quantity}, Price: ₹{book.totalPrice.toLocaleString()}
                                                        </div>
                                                    ))}
                                                    <div className="mt-2 p-2 bg-light rounded">
                                                        <strong>Books Total: ₹{price?.toLocaleString()}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Temple pickup information */}
                                    {deliveryOption === "pickup" && (
                                        <div className="col-12">
                                            <div className="alert alert-info">
                                                <h5>Temple Pickup Information</h5>
                                                <p>
                                                    <strong>Address:</strong>{" "}
                                                    <a
                                                        href="https://www.google.com/maps/place/ISKCON+Attapur/@17.3602347,78.4325114,791m/data=!3m2!1e3!4b1!4m6!3m5!1s0x3bcb97170a21438d:0x6dbda5e3d7260b5b!8m2!3d17.3602347!4d78.4325114!16s%2Fg%2F11qyq8tmvr"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: "#0c5460", fontWeight: "bold", textDecoration: "underline" }}
                                                    >
                                                        ISKCON Temple, Attapur, Hyderabad
                                                    </a>
                                                </p>
                                                <p><strong>Pickup Hours:</strong> 9:00 AM - 12:00 PM and 4:00 PM - 8:00 PM</p>
                                                <p>
                                                    <strong>Contact:</strong>{" "}
                                                    <a href="tel:+919391250326">9391250326</a>,{" "}
                                                    <a href="tel:+916304321935">6304321935</a>
                                                </p>

                                                <p className="mb-0">
                                                    <em>Please bring your order confirmation and valid ID for pickup.</em>
                                                </p>
                                            </div>
                                        </div>
                                    )}


                                    <div className="col-12">
                                        <div className="col-12">
                                            <ReCAPTCHA sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY} ref={reCaptChaRef} size="invisible" />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <button type="submit" className="box-hover custom-btn-cls donation_btn ms-0 donate-now-clicked-form">
                                            {loading ? (
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border text-light" role="status"></div>
                                                    <div className="ms-2">PURCHASING...</div>
                                                </div>
                                            ) : (
                                                <>PAY ₹{totalPrice.toLocaleString()}/-</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </section>
            {/* <GoToTop top={top} /> */}
        </>
    );
};

export default BhadraPurnimaCheckout;